import { amount } from "./gameMath.ts";
import type { CurrencyAmount } from "./types.ts";
import { boostedRecoveryElapsed, type RecoveryBoost } from "./energyBoost.ts";

const BASIS_POINTS = 10_000n;

export type EnergyRecoveryInput = {
  readonly current: number;
  readonly capacity: number;
  readonly recoverySecondsPerUnit: number;
  readonly lastEnergyAt: string;
  readonly remainderMilliseconds: number;
  readonly boosts?: readonly RecoveryBoost[];
};

export type EnergyRecoveryResult = {
  readonly current: number;
  readonly restored: number;
  readonly lastEnergyAt: string;
  readonly remainderMilliseconds: number;
};

function safeInteger(value: number, name: string): void {
  if (!Number.isSafeInteger(value) || value < 0) throw new Error(`${name} must be a non-negative safe integer`);
}

export function utcDayKey(date: Date): string {
  if (Number.isNaN(date.getTime())) throw new Error("Date must be valid");
  return date.toISOString().slice(0, 10);
}

export function recoverEnergy(input: EnergyRecoveryInput, now: Date): EnergyRecoveryResult {
  safeInteger(input.current, "current energy");
  safeInteger(input.capacity, "energy capacity");
  safeInteger(input.recoverySecondsPerUnit, "energy recovery interval");
  safeInteger(input.remainderMilliseconds, "energy remainder");
  if (input.capacity < 1 || input.recoverySecondsPerUnit < 1) throw new Error("Energy configuration must be positive");

  const nowMilliseconds = now.getTime();
  if (Number.isNaN(nowMilliseconds)) throw new Error("Current time must be valid");
  const boundedCurrent = Math.min(input.current, input.capacity);
  const lastMilliseconds = new Date(input.lastEnergyAt).getTime();
  const nowIso = now.toISOString();

  if (Number.isNaN(lastMilliseconds) || lastMilliseconds > nowMilliseconds || boundedCurrent >= input.capacity) {
    return {
      current: boundedCurrent,
      restored: 0,
      lastEnergyAt: nowIso,
      remainderMilliseconds: 0,
    };
  }

  const intervalMilliseconds = input.recoverySecondsPerUnit * 1_000;
  const elapsedMilliseconds = Number(boostedRecoveryElapsed(lastMilliseconds, nowMilliseconds, input.boosts ?? []));
  const availableMilliseconds = elapsedMilliseconds + Math.min(input.remainderMilliseconds, intervalMilliseconds - 1);
  const possibleRestored = Math.floor(availableMilliseconds / intervalMilliseconds);
  const current = Math.min(input.capacity, boundedCurrent + possibleRestored);
  const restored = current - boundedCurrent;

  return {
    current,
    restored,
    lastEnergyAt: nowIso,
    remainderMilliseconds: current >= input.capacity ? 0 : availableMilliseconds % intervalMilliseconds,
  };
}

export function secondsUntilNextEnergy(
  current: number,
  capacity: number,
  recoverySecondsPerUnit: number,
  remainderMilliseconds: number,
  multiplierBasisPoints = 10000,
): number {
  safeInteger(current, "current energy");
  safeInteger(capacity, "energy capacity");
  safeInteger(recoverySecondsPerUnit, "energy recovery interval");
  safeInteger(remainderMilliseconds, "energy remainder");
  if (current >= capacity) return 0;
  const intervalMilliseconds = recoverySecondsPerUnit * 1_000;
  safeInteger(multiplierBasisPoints, "energy recovery multiplier");
  if (multiplierBasisPoints < 10000 || multiplierBasisPoints > 100000) throw new Error("Invalid recovery multiplier");
  return Math.max(1, Math.ceil((intervalMilliseconds - Math.min(remainderMilliseconds, intervalMilliseconds - 1)) * 10000 / multiplierBasisPoints / 1_000));
}

export function quoteEnergyRefillCost(
  missingEnergy: number,
  tapReward: CurrencyAmount,
  priceBasisPoints: number,
): CurrencyAmount {
  safeInteger(missingEnergy, "missing energy");
  safeInteger(priceBasisPoints, "refill price basis points");
  if (priceBasisPoints < 1) throw new Error("Refill price must be positive");
  if (missingEnergy === 0) return "0";
  const numerator = BigInt(missingEnergy) * amount(tapReward) * BigInt(priceBasisPoints);
  return ((numerator + BASIS_POINTS - 1n) / BASIS_POINTS).toString();
}

export function energyFillPercent(current: number, capacity: number): number {
  if (capacity <= 0) return 0;
  return Math.max(0, Math.min(100, (current / capacity) * 100));
}
